import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { API_ROUTES } from '@/constants';
import { useEventSource } from '@/hooks/useEventSource';
import { type ChatMessageData, createMessage } from '../_utils/message';
const STREAM_ERROR_MESSAGE = 'AI의 응답을 받는 중 오류가 발생했습니다.\n잠시 후 다시 시도해주세요.';
const SESSION_NOT_READY_MESSAGE = '세션이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.';
const RETRY_LAST_OPTION = [{
  value: 'retry_last',
  label: '다시 시도하기'
}];
const RETRY_SESSION_OPTION = [{
  value: 'retry_session',
  label: '다시 시도하기'
}];
const STREAM_WATCHDOG_MS = 45_000;
interface SendMessageOptions {
  displayUserMessage?: boolean;
}
interface UseChatStreamParams {
  sessionId: string | null;
  userId?: string;
  onAddMessages: (messages: ChatMessageData[]) => void;
  onUpdateMessage: (updates: Partial<ChatMessageData>) => void;
}
export const useChatStream = t0 => {
  const $ = _c(36);
  const {
    sessionId,
    userId,
    onAddMessages,
    onUpdateMessage
  } = t0;
  const contentBufferRef = useRef("");
  const hasStreamFailedRef = useRef(false);
  const lastRequestRef = useRef(null);
  const watchdogRef = useRef(null);
  const [isToolRunning, setIsToolRunning] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearWatchdog = t1;
  let t2;
  if ($[1] !== onUpdateMessage) {
    t2 = message => {
      hasStreamFailedRef.current = true;
      clearWatchdog();
      const partialContent = contentBufferRef.current.trim();
      onUpdateMessage({
        ...(partialContent ? {} : {
          content: ""
        }),
        isStreaming: false,
        isMarkdown: !!partialContent,
        isCopy: false,
        errorText: message || STREAM_ERROR_MESSAGE,
        options: RETRY_LAST_OPTION
      });
      setIsToolRunning(false);
      contentBufferRef.current = "";
    };
    $[1] = onUpdateMessage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const failStream = t2;
  const armWatchdog = () => {
    clearWatchdog();
    watchdogRef.current = window.setTimeout(() => {
      closeStream();
      failStream();
    }, STREAM_WATCHDOG_MS);
  };
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["done"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onUpdateMessage) {
    t4 = () => {
      clearWatchdog();
      setIsToolRunning(false);
      if (hasStreamFailedRef.current) {
        contentBufferRef.current = "";
        return;
      }
      onUpdateMessage({
        isStreaming: false,
        isMarkdown: true,
        isCopy: true,
        errorText: undefined,
        options: undefined
      });
      contentBufferRef.current = "";
    };
    $[4] = onUpdateMessage;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== failStream) {
    t5 = message_0 => failStream(message_0);
    $[6] = failStream;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== failStream) {
    t6 = () => failStream();
    $[8] = failStream;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    start: startStreaming,
    close: t7,
    isStreaming
  } = useEventSource({
    closeOnEvents: t3,
    eventHandlers: {
      done: t4,
      error: t5,
      tool_start: () => {
        armWatchdog();
        setIsToolRunning(true);
      },
      tool_end: () => {
        armWatchdog();
        setIsToolRunning(false);
      }
    },
    onError: t6,
    onMessage: chunk => {
      if (!chunk) {
        return;
      }
      armWatchdog();
      contentBufferRef.current = contentBufferRef.current + chunk;
      onUpdateMessage({
        content: contentBufferRef.current,
        isStreaming: true
      });
    }
  });
  const closeStream = t7;
  let t8;
  if ($[10] !== armWatchdog || $[11] !== startStreaming || $[12] !== userId) {
    t8 = (input, effectiveSessionId) => {
      contentBufferRef.current = "";
      hasStreamFailedRef.current = false;
      setIsToolRunning(false);
      startStreaming({
        url: API_ROUTES.WELLO_AGENT_CHAT_COMPLETIONS_STREAM.withSearchParams({
          searchParams: {
            message: input,
            session_id: effectiveSessionId,
            user_id: userId
          }
        })
      });
      armWatchdog();
    };
    $[10] = armWatchdog;
    $[11] = startStreaming;
    $[12] = userId;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const startMessageStream = t8;
  let t9;
  if ($[14] !== isStreaming || $[15] !== onAddMessages || $[16] !== onUpdateMessage || $[17] !== sessionId || $[18] !== startMessageStream) {
    t9 = (input_0, sessionIdOverride, options) => {
      const trimmedInput = input_0.trim();
      if (!trimmedInput || isStreaming) {
        return;
      }
      const effectiveSessionId_0 = sessionIdOverride ?? sessionId;
      const displayUserMessage = options?.displayUserMessage ?? true;
      onAddMessages([...(displayUserMessage ? [createMessage("user", {
        content: trimmedInput
      })] : []), createMessage("ai", {
        isMarkdown: true,
        isStreaming: true
      })]);
      if (!effectiveSessionId_0) {
        onUpdateMessage({
          content: SESSION_NOT_READY_MESSAGE,
          isCopy: false,
          isMarkdown: false,
          isStreaming: false,
          options: RETRY_SESSION_OPTION
        });
        return;
      }
      lastRequestRef.current = {
        input: trimmedInput,
        sessionId: effectiveSessionId_0
      };
      startMessageStream(trimmedInput, effectiveSessionId_0);
    };
    $[14] = isStreaming;
    $[15] = onAddMessages;
    $[16] = onUpdateMessage;
    $[17] = sessionId;
    $[18] = startMessageStream;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const sendMessage = t9;
  let t10;
  if ($[20] !== closeStream || $[21] !== isStreaming || $[22] !== onUpdateMessage) {
    t10 = () => {
      if (!isStreaming) {
        return;
      }
      closeStream();
      clearWatchdog();
      onUpdateMessage({
        isStreaming: false,
        isMarkdown: true,
        isCopy: !!contentBufferRef.current.trim(),
        errorText: undefined,
        options: undefined
      });
      setIsToolRunning(false);
      contentBufferRef.current = "";
    };
    $[20] = closeStream;
    $[21] = isStreaming;
    $[22] = onUpdateMessage;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const stopStreaming = t10;
  let t11;
  if ($[24] !== isStreaming || $[25] !== onUpdateMessage || $[26] !== startMessageStream) {
    t11 = () => {
      const lastRequest = lastRequestRef.current;
      if (!lastRequest || isStreaming) {
        return;
      }
      onUpdateMessage({
        content: "",
        isCopy: false,
        isMarkdown: true,
        isStreaming: true,
        errorText: undefined,
        options: undefined
      });
      startMessageStream(lastRequest.input, lastRequest.sessionId);
    };
    $[24] = isStreaming;
    $[25] = onUpdateMessage;
    $[26] = startMessageStream;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const retryLastMessage = t11;
  let t12;
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => () => {
      if (watchdogRef.current !== null) {
        window.clearTimeout(watchdogRef.current);
      }
    };
    t13 = [];
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== isStreaming || $[31] !== isToolRunning || $[32] !== retryLastMessage || $[33] !== sendMessage || $[34] !== stopStreaming) {
    t14 = {
      isStreaming,
      isToolRunning,
      retryLastMessage,
      sendMessage,
      stopStreaming
    };
    $[30] = isStreaming;
    $[31] = isToolRunning;
    $[32] = retryLastMessage;
    $[33] = sendMessage;
    $[34] = stopStreaming;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};